import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  message,
  Row,
  Segmented,
  Space,
  Spin,
  Table,
  Tooltip,
  Upload,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import api from "../../../api";
import RejectionModal from "../Approval/RejectionModal";

function Sponsor() {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [toggleUser, setToggleUser] = useState("sponsors");

  // ✅ Sponsor states
  const [sponsors, setSponsors] = useState([]);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [tempSponsor, setTempSponsor] = useState({
    name: "",
    hyperlink: "",
    fileList: [],
    preview: "",
  });
  const [loadingSponsors, setLoadingSponsors] = useState(false);

  // ✅ Brand states
  const [serviceName, setServiceName] = useState("");
  const [services, setServices] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loading, setLoading] = useState(false);

  const [openSponsorDeleteModal, setOpenSponsorDeleteModal] = useState(false);
  const [openBrandDeleteModal, setOpenBrandDeleteModal] = useState(false);
  const [selectedDeleteRecord, setSelectedDeleteRecord] = useState(null);

  // ✅ Common Helper
  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const validateImageFormat = (file) => {
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const valid = allowed.includes(file.type);
    if (!valid) messageApi.error("Only JPG, JPEG, PNG, or WebP allowed!");
    return valid;
  };

  // ================= SPONSOR API =================
  const fetchSponsors = async () => {
    setLoadingSponsors(true);
    try {
      const requestData = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
      };

      const res = await api.getSponsorCreation(requestData);
      if (res?.data?.status === "1") {
        if (res?.data?.sponsor_data) {
          const formatted = res.data.sponsor_data.map((item) => ({
            key: item.fld_sponsor_id,
            fld_rf_id: item.fld_rf_id,
            fld_sponsor_id: item.fld_sponsor_id,
            name: item.fld_sponsor_name,
            hyperlink: item.fld_url,
            preview: item.fld_logo_path,
          }));

          setSponsors(
            formatted.sort(
              (a, b) => Number(b.fld_sponsor_id) - Number(a.fld_sponsor_id)
            )
          );
        }
      }
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to fetch sponsors");
    } finally {
      setLoadingSponsors(false);
    }
  };

  const handleAddOrUpdateSponsor = async () => {
    const { name, hyperlink, preview, fileList } = tempSponsor;

    if (!name || !preview) {
      messageApi.warning("Please upload logo and enter sponsor name");
      return;
    }

    const formData = new FormData();

    // ✅ Properly handle rf_id and sponsor_id for editing
    if (editingSponsor) {
      formData.append("fld_rf_id", editingSponsor.fld_rf_id || "");
      formData.append("fld_sponsor_id", editingSponsor.fld_sponsor_id || "");
    } else {
      formData.append("fld_rf_id", "");
      formData.append("fld_sponsor_id", "");
    }

    formData.append("fld_sponsor_name", name);
    formData.append("fld_url", hyperlink || "");
    formData.append("fld_loggedin_user_id", "admin");
    formData.append("fld_data_source", "W");

    // ✅ Logo handling logic:
    // 1. If there's a new file uploaded → send as FILE
    // 2. If editing and logo unchanged (URL) → send as URL
    // 3. Otherwise → error

    const uploadedFile = fileList?.[0]?.originFileObj;

    if (uploadedFile) {
      // Case 1: New file uploaded (adding new sponsor OR changing logo during edit)
      formData.append("fld_logo_path", uploadedFile);
    } else if (editingSponsor && preview && !preview.startsWith("data:image")) {
      // Case 2: Editing, no new file, preview is existing URL from backend

      formData.append("fld_logo_path", preview);
    } else {
      // Case 3: Invalid state - should not happen
      messageApi.error("Invalid logo state. Please upload a logo.");
      return;
    }

    try {
      setLoadingSponsors(true);


      const res = await api.sponsorCreation(formData);

      if (res?.data?.status === "1") {
        messageApi.success(
          editingSponsor ? "Sponsor updated successfully!" : "Sponsor added successfully!"
        );
        fetchSponsors();
        setEditingSponsor(null);
        setTempSponsor({ name: "", hyperlink: "", fileList: [], preview: "" });
        form.resetFields();
      } else {
        messageApi.error(res?.data?.message || "Failed to save sponsor");
      }
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to save sponsor");
    } finally {
      setLoadingSponsors(false);
    }
  };

  const handleDeleteSponsor = async () => {
    try {
      const payload = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
        fld_loggedin_user_id: "admin",
        fld_data_source: "W",
        fld_rf_id: selectedDeleteRecord.fld_rf_id,
        fld_reason_for_deletion: "",
      };

      const res = await api.deleteCreation(payload);
      if (res.data.status === "1") {
        messageApi.success("Sponsor deleted!");
        fetchSponsors();
        setEditingSponsor(null);
        form.resetFields();
        setTempSponsor({ name: "", hyperlink: "", fileList: [], preview: "" });
        setOpenSponsorDeleteModal(false);
      } else {
        messageApi.error(res?.data?.message || "Failed to delete sponsor");
      }
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to delete sponsor");
    }
  };

  const handleEditSponsor = (record) => {
    setEditingSponsor(record);

    setTempSponsor({
      name: record.name,
      hyperlink: record.hyperlink,
      fileList: [],
      preview: record.preview,
    });
    form.setFieldsValue({
      name: record.name,
      hyperlink: record.hyperlink,
    });
  };

  const handleCancelEdit = () => {
    setEditingSponsor(null);
    setTempSponsor({ name: "", hyperlink: "", fileList: [], preview: "" });
    form.resetFields();
  };

  const sponsorColumns = [
    {
      title: "Logo",
      dataIndex: "preview",
      key: "logo",
      render: (src) =>
        src ? (
          <img
            src={src}
            alt="logo"
            style={{ width: 50, height: 50, borderRadius: 4, objectFit: "cover" }}
          />
        ) : (
          "-"
        ),
    },
    { title: "Sponsor Name", dataIndex: "name", key: "name" },
    {
      title: "Website",
      dataIndex: "hyperlink",
      key: "hyperlink",
      render: (text) => {
        if (!text) return "-";

        // Add https:// if missing
        const validUrl = text.startsWith("http://") || text.startsWith("https://")
          ? text
          : `https://${text}`;

        return (
          <a href={validUrl} target="_blank" rel="noreferrer">
            {text}
          </a>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size={8} className="category-actions">
          <Tooltip title="Edit Sponsor">
            <Button
              icon={<EditOutlined />}
              size="small"
              style={{
                borderRadius: 6,
                borderColor: "#ccc",
                color: "#555",
                background: "white",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onClick={() => handleEditSponsor(record)}
            >
              Edit
            </Button>
          </Tooltip>

          <Tooltip title="Delete Sponsor">
            <Button
              icon={<DeleteOutlined />}
              size="small"
              style={{
                borderRadius: 6,
                borderColor: "#ccc",
                color: "#d9534f",
                background: "white",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onClick={() => {
                setSelectedDeleteRecord(record);
                setOpenSponsorDeleteModal(true);
              }}
            >
              Delete
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ================= BRAND API =================
  const fetchBrands = async () => {
    setLoadingServices(true);
    try {
      const requestData = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
      };
      const res = await api.getBrand(requestData);

      if (res?.data?.brand_data) {
        const formatted = res.data.brand_data.map((item) => ({
          key: item.fld_brand_id,
          fld_rf_id: item.fld_rf_id,
          name: item.fld_brand_name,
        }));

        setServices(
          formatted.sort(
            (a, b) => Number(b.key) - Number(a.key)
          )
        );
      }
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to fetch brands");
    } finally {
      setLoadingServices(false);
    }
  };

  const handleAddService = async () => {
    if (!serviceName.trim()) {
      message.warning("Please enter a brand name!");
      return;
    }

    const payload = {
      master_tbl_brand: [
        {
          fld_rf_id: editingService?.fld_rf_id || "",
          fld_brand_id: editingService?.key || "",
          fld_brand_name: serviceName.trim(),
          fld_loggedin_user_id: "admin",
          fld_data_source: "W",
        },
      ],
      synceddatetime: "2025-10-20 10:00:00",
      FormCode: "201",
      AppVersion: "1.0.0.0",
      ApiKey: "kavin",
      AppTypeNo: "3",
    };

    try {
      setLoading(true);
      await api.brandCreation(payload);
      message.success(
        editingService ? "Brand updated successfully!" : "Brand added successfully!"
      );
      fetchBrands();
      setEditingService(null);
      setServiceName("");
    } catch (err) {
      console.error(err);
      message.error("Failed to save brand");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async () => {
    try {
      const payload = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
        fld_loggedin_user_id: "admin",
        fld_data_source: "W",
        fld_rf_id: selectedDeleteRecord.fld_rf_id,
        fld_reason_for_deletion: "",
      };
      const res = await api.deleteBrand(payload);
      if (res.data.status === "1") {
        messageApi.success("Brand deleted!");
        fetchBrands();
        setOpenBrandDeleteModal(false);
        setServiceName('')
        setEditingService(null);
      } else {
        messageApi.error(res?.data?.message || "Failed to delete brand");
      }
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to delete brand");
    }
  };

  const handleEditService = (record) => {
    setEditingService(record);
    setServiceName(record.name);
  };

  const handleCancelServiceEdit = () => {
    setEditingService(null);
    setServiceName("");
  };

  const serviceColumns = [
    { title: "Brand Name", dataIndex: "name", key: "name" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size={8} className="category-actions">
          <Tooltip title="Edit Brand">
            <Button
              icon={<EditOutlined />}
              size="small"
              style={{
                borderRadius: 6,
                borderColor: "#ccc",
                color: "#555",
                background: "white",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onClick={() => handleEditService(record)}
            >
              Edit
            </Button>
          </Tooltip>

          <Tooltip title="Delete Brand">
            <Button
              icon={<DeleteOutlined />}
              size="small"
              style={{
                borderRadius: 6,
                borderColor: "#ccc",
                color: "#d9534f",
                background: "white",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onClick={() => {
                setSelectedDeleteRecord(record);
                setOpenBrandDeleteModal(true);
              }}
            >
              Delete
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ================= LOAD DATA =================
  useEffect(() => {
    if (toggleUser === "sponsors") {
      fetchSponsors();
    } else {
      fetchBrands();
    }
  }, [toggleUser]);

  // ================= RETURN =================
  return (
    <div className="sponsor-container">
      {contextHolder}

      <Segmented
        options={[
          { label: "Sponsors", value: "sponsors" },
          { label: "Brands", value: "brands" },
        ]}
        value={toggleUser}
        onChange={setToggleUser}
        className="category-custom-segmented"
        style={{ marginBottom: 16 }}
      />

      {toggleUser === "sponsors" ? (
        <>
          <Form layout="vertical" form={form}>
            <Row gutter={12} align="middle" style={{ marginBottom: 16 }}>
              <Col xs={24} sm={3}>
                <Form.Item label="Logo">
                  <Upload
                    listType="picture-card"
                    maxCount={1}
                    showUploadList={false}
                    beforeUpload={(file) => {
                      if (!validateImageFormat(file)) return false;
                      getBase64(file).then((base64) => {
                        setTempSponsor((prev) => ({
                          ...prev,
                          preview: base64,
                          fileList: [{ originFileObj: file }] // ✅ Store file object
                        }));
                      });
                      return false;
                    }}
                  >
                    {tempSponsor.preview ? (
                      <img
                        src={tempSponsor.preview}
                        alt="Sponsor Logo"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    ) : (
                      <Flex vertical align="center" justify="center">
                        <UploadOutlined style={{ fontSize: 15 }} />
                        <div style={{ fontSize: 12 }}>Upload</div>
                      </Flex>
                    )}
                  </Upload>
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item label="Sponsor Name" name="name">
                  <Input
                    placeholder="e.g., Tech Corp"
                    value={tempSponsor.name}
                    onChange={(e) =>
                      setTempSponsor({
                        ...tempSponsor,
                        name: e.target.value,
                      })
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item label="Website URL" name="hyperlink">
                  <Input
                    placeholder="e.g., https://techcorp.com"
                    value={tempSponsor.hyperlink}
                    onChange={(e) =>
                      setTempSponsor({
                        ...tempSponsor,
                        hyperlink: e.target.value,
                      })
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={5}>
                <Space>
                  <Button
                    type="primary"
                    icon={editingSponsor ? <EditOutlined /> : <PlusOutlined />}
                    style={{ backgroundColor: "#0072B1" }}
                    onClick={handleAddOrUpdateSponsor}
                    loading={loadingSponsors}
                  >
                    {editingSponsor ? "Update" : "Add"}
                  </Button>
                  {editingSponsor && (
                    <Button onClick={handleCancelEdit}>Cancel</Button>
                  )}
                </Space>
              </Col>
            </Row>
          </Form>

          <div className="category-section-title-small">Sponsors List</div>
          <Spin spinning={loadingSponsors}>
            <Table
              columns={sponsorColumns}
              dataSource={sponsors}
              bordered
              pagination={{
                pageSizeOptions: ["5", "10"],
                showSizeChanger: true,
                defaultPageSize: 5,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </Spin>
        </>
      ) : (
        <div style={{ backgroundColor: "#fff" }}>
          <Card className="category-card">
            <div className="category-label">Brand</div>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                placeholder="Enter brand name"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                allowClear
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ backgroundColor: "#0072B1" }}
                onClick={handleAddService}
                loading={loading}
              >
                {editingService ? "Update" : "Add"}
              </Button>
              {editingService && (
                <Button onClick={handleCancelServiceEdit}>Cancel</Button>
              )}
            </Space.Compact>
          </Card>

          <div className="category-list-section">
            <div className="category-section-title-small">Brand List</div>
            <Spin spinning={loadingServices}>
              <Table
                columns={serviceColumns}
                dataSource={services}
                bordered
                pagination={{
                  pageSizeOptions: ["5", "10"],
                  showSizeChanger: true,
                  defaultPageSize: 5,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                }}
              />
            </Spin>
          </div>
        </div>
      )}

      {openSponsorDeleteModal && (
        <RejectionModal
          title="Delete Sponsor"
          content="Are you sure you want to delete this sponsor? This cannot be undone."
          buttonName="Delete"
          open={openSponsorDeleteModal}
          onClose={() => setOpenSponsorDeleteModal(false)}
          onSubmit={handleDeleteSponsor}
        />
      )}

      {openBrandDeleteModal && (
        <RejectionModal
          title="Delete Brand"
          content="Are you sure you want to delete this brand? This cannot be undone."
          buttonName="Delete"
          open={openBrandDeleteModal}
          onClose={() => setOpenBrandDeleteModal(false)}
          onSubmit={handleDeleteService}
        />
      )}
    </div>
  );
}

export default Sponsor;